import { type FC } from 'react';
import { SWRConfig } from 'swr';

import { useDeviceType, Container, ErrorBoundary, Box, Divider } from '@packages/shared';
import { useConfig, useCookies } from '@packages/utilities';

import { headerDefaultFetcher } from './api';
import { useSearchRoute } from './utils';
import { usePaceId } from './usePaceId';
import { AppBanner } from './AppBanner';
import { DesktopNavigation, MobileNavigation } from './Navigation';
import { ResponsiveNavigation } from './Navigation/ResponsiveNavigation';
import { HeaderToolbar } from './Toolbar/HeaderToolbar';
import { DynamicSearchField } from './Toolbar/SearchField';
import { ArrowBox, SlotsDefault, SlotsMinimal } from './Toolbar/Slots';
import { Usp } from './Usp';
import { InspiringSearchOnboardingModal } from '../InspiringSearch/InspiringSearchOnboardingModal';

const SEARCH_ICON_MOBILE_RIGHT_POSITION = '123px';

export type HeaderProps = {
  isMinimal?: boolean;
};

const MinimalHeader: FC = () => (
  // TODO: find out if this is even necessary without navigation in the minimal header, unsure why this default fetcher exists
  <SWRConfig value={{ fetcher: headerDefaultFetcher }}>
    {/* When no inner error boundary catches an error, this is the global boundary for the header. It exists only as backup. When possible, wrap every logical section with a boundary so this one will never show its fallback. */}
    <ErrorBoundary>
      <Container
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          boxShadow: [1, 1, 1, 0],
          px: [0, 0, 0, 1, 0],
        }}
      >
        <hr />
        <HeaderToolbar>
          <ErrorBoundary>
            <SlotsMinimal />
          </ErrorBoundary>
        </HeaderToolbar>
      </Container>

      <Divider sx={{ borderColor: 'grey.main', display: { xs: 'none', lg: 'block' } }} />
    </ErrorBoundary>
  </SWRConfig>
);

/**
 * Integrated header module with appbanner (only mobile), toolbar and navigation.
 * This component is able to get its data whether from client or it accepts the SEO relevant initial data from server to populate SWR cache and to make its further fetches and revalidations on client.
 */
const DefaultHeader: FC = () => {
  const {
    header: { hasNewNavigation, desktopNavigationMode },
  } = useConfig();
  const { isMobile, isDesktop } = useDeviceType();

  const { getCookies } = useCookies();
  const cookies = getCookies();

  const searchQuery = useSearchRoute();

  return (
    <SWRConfig value={{ fetcher: headerDefaultFetcher }}>
      {/* When no inner error boundary catches an error, this is the global boundary for the header. It exists only as backup. When possible, wrap every logical section with a boundary so this one will never show its fallback. */}
      <ErrorBoundary>
        {isMobile && <AppBanner />}
        <Container
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            boxShadow: [1, 1, 1, 0],
            px: [0, 0, 0, 1, 0],
          }}
        >
          {!isDesktop && (
            <ErrorBoundary>
              <MobileNavigation />
            </ErrorBoundary>
          )}
          <HeaderToolbar>
            {isDesktop && (
              <Box sx={{ flexGrow: 1 }}>
                <ErrorBoundary>
                  <DynamicSearchField />
                </ErrorBoundary>
              </Box>
            )}
            <ErrorBoundary>
              <SlotsDefault />
            </ErrorBoundary>
          </HeaderToolbar>
        </Container>

        {isDesktop && (
          <ErrorBoundary>
            {hasNewNavigation || 'feature/newNavigation' in cookies ? (
              // for consistency with the old navigation, hoverMode was chosen as the default for now
              <ResponsiveNavigation mode={desktopNavigationMode} />
            ) : (
              <DesktopNavigation />
            )}
          </ErrorBoundary>
        )}
        {isDesktop && (
          <Container>
            <ErrorBoundary>
              <Usp />
            </ErrorBoundary>
          </Container>
        )}
        {/* for non-SERP routes, the SearchField is displayed via SearchSlot.tsx */}
        {/* fixed position of the arrow on top of the box */}
        {!isDesktop && searchQuery && (
          <ErrorBoundary>
            <ArrowBox left={`calc(100% - ${SEARCH_ICON_MOBILE_RIGHT_POSITION})`}>
              <DynamicSearchField />
            </ArrowBox>
          </ErrorBoundary>
        )}

        <ErrorBoundary>
          <InspiringSearchOnboardingModal />
        </ErrorBoundary>
      </ErrorBoundary>
    </SWRConfig>
  );
};

export const Header: FC<HeaderProps> = ({ isMinimal }) => {
  usePaceId();

  return isMinimal ? <MinimalHeader /> : <DefaultHeader />;
};
